// src/app/providers/EmpresaProvider.jsx
import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
} from "react";

const EmpresaContext = createContext(null);

const EMPRESA_KEY = "sabi_empresa_id";
const EMPRESA_NOMBRE_KEY = "sabi_empresa_nombre";
const SUCURSAL_KEY = "sabi_sucursal_id";
const EMPRESAS_LEGACY_KEY = "sabi_empresa";

function safeParseJsonArray(raw) {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw);
    return Array.isArray(v) ? v : null;
  } catch {
    return null;
  }
}

function pickEmpresaFromLegacyStorage() {
  const legacy = safeParseJsonArray(localStorage.getItem(EMPRESAS_LEGACY_KEY));
  if (!legacy || legacy.length === 0) return { id: "", nombre: "" };
  const first = legacy[0] || {};
  const id = first.id != null ? String(first.id) : "";
  const nombre = String(first.name || first.nombre || "");
  return { id, nombre };
}

export function EmpresaProvider({ children }) {
  const [empresaId, setEmpresaIdState] = useState(() => {
    const current = localStorage.getItem(EMPRESA_KEY) || "";
    if (current) return current;
    return pickEmpresaFromLegacyStorage().id;
  });

  const [empresaNombre, setEmpresaNombreState] = useState(() => {
    const current = localStorage.getItem(EMPRESA_NOMBRE_KEY) || "";
    if (current) return current;
    return pickEmpresaFromLegacyStorage().nombre;
  });

  const setEmpresaNombre = useCallback((nombre) => {
    const v = nombre ? String(nombre) : "";
    setEmpresaNombreState(v);
    if (v) localStorage.setItem(EMPRESA_NOMBRE_KEY, v);
    else localStorage.removeItem(EMPRESA_NOMBRE_KEY);
  }, []);

  const [sucursalId, setSucursalIdState] = useState("");

  const setEmpresaId = useCallback((id) => {
    const v = id ? String(id) : "";
    setEmpresaIdState(v);
    if (v) localStorage.setItem(EMPRESA_KEY, v);
    else localStorage.removeItem(EMPRESA_KEY);
  }, []);

  const setSucursalId = useCallback((id) => {
    const v = id ? String(id) : "";
    setSucursalIdState(v);
    localStorage.removeItem(SUCURSAL_KEY);
  }, []);

  const value = useMemo(
    () => ({
      empresaId,
      empresaNombre,
      sucursalId,
      setEmpresaId,
      setEmpresaNombre,
      setSucursalId,
    }),
    [empresaId, empresaNombre, sucursalId, setEmpresaId, setEmpresaNombre, setSucursalId]
  );

  return (
    <EmpresaContext.Provider value={value}>{children}</EmpresaContext.Provider>
  );
}

export function useEmpresa() {
  const ctx = useContext(EmpresaContext);
  if (!ctx)
    throw new Error("useEmpresa debe usarse dentro de <EmpresaProvider />");
  return ctx;
}
